import { useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import AuthLayout from '../../components/AuthLayout'
import ImpersonationBanner from '../../components/layout/ImpersonationBanner'
import { SharedProps } from '../../types'

interface Workspace {
  id: number
  name: string
  slug: string
  user_id: number
}

interface Props {
  workspaces: Workspace[]
}

export default function Select({ workspaces }: Props) {
  const [processing, setProcessing] = useState(false)
  const { csrf_token, can_access_guide_billing } = usePage().props as unknown as SharedProps

  const signOut = () => router.delete('/users/sign_out', { data: { authenticity_token: csrf_token ?? '' } })

  // Post the id directly: useForm's setData is asynchronous, so calling
  // setData + post in the same handler submits the stale initial value.
  const handleSelect = (orgId: number) => {
    router.post(
      '/workspaces',
      { organization_id: orgId },
      {
        onStart: () => setProcessing(true),
        onFinish: () => setProcessing(false),
      }
    )
  }

  return (
    <>
      <Head title="Select a Workspace - Pinnacle" />
      <ImpersonationBanner />
      <AuthLayout>
        <p
          className="font-serif italic text-2xl text-text-primary text-center mb-8"
          style={{ color: '#1B1B1B' }}
        >
          Choose a workspace.
        </p>

        {can_access_guide_billing && (
          <div className="mb-6 rounded-lg border border-stone/30 bg-stone/5 p-4 space-y-3">
            <p className="font-sans text-sm text-text-muted">Manage your Guide portfolio without selecting a company workspace.</p>
            <div className="flex flex-wrap gap-3">
              <Link href="/guide_billing" className="px-4 py-2 rounded-button bg-pinnacle-red text-white font-sans text-sm font-semibold">Guide Billing</Link>
              <Link href="/guide_tenants/new" className="px-4 py-2 rounded-button border border-stone/30 text-text-primary font-sans text-sm font-semibold hover:bg-stone/10">Create Company</Link>
            </div>
          </div>
        )}

        {workspaces.length === 0 ? (
          <div className="text-center">
            <p className="font-sans text-base text-text-muted mb-4">
              You don&apos;t have access to any active workspaces.
            </p>
            <button
              type="button"
              onClick={signOut}
              className="text-text-primary hover:underline font-medium"
            >
              Sign out
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {workspaces.map((ws) => (
              <button
                key={ws.id}
                onClick={() => handleSelect(ws.id)}
                disabled={processing}
                className="w-full text-left px-5 py-4 rounded-button border border-stone/30 hover:border-pinnacle-red hover:bg-pinnacle-red/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="font-sans text-base font-medium text-text-primary">{ws.name}</span>
              </button>
            ))}
          </div>
        )}

        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={signOut}
            className="font-sans text-sm text-text-muted hover:text-text-primary hover:underline"
          >
            Sign out
          </button>
        </div>
      </AuthLayout>
    </>
  )
}
